import type { FeatureName, GlobalFeatureName } from 'owa-feature-flags';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { ResolverEnabledFor, getApplicationSettings } from 'owa-application-settings';
import {
    type MutationResolvers,
    type QueryResolvers,
    type SubscriptionResolvers,
    type ConversationResolvers,
} from 'owa-graph-schema';

type ResolverNames =
    | keyof MutationResolvers
    | keyof QueryResolvers
    | keyof SubscriptionResolvers
    | keyof ConversationResolvers;

// Repeating type defintion from owa-graph-schema to avoid circular dependency
type ResolverPolicy =
    | 'localFirst' // Try the idb resolver first and fall back to the web resolver if the idb resolver fails
    | 'serverFirst' // Try the web resolver first and fall back to the idb resolver if the web resolver fails
    | 'serverOnly' // Only use the web resolver
    | 'localOnly' // Only use the idb resolver
    | 'race'; // Call both the web and idb resolvers at the same time and use whichever one returns first

const IndividualResolverFeatureMappings: Map<ResolverNames, FeatureName> = new Map([
    ['markItemAsRead', 'fwk-useoutlookgateway-markItemAsRead'],
    ['collab', 'cal-win32-useoutlookgateway-collabObject'],
    ['getScheduleWithLocation', 'msplaces-useOutlookGatewayReferenceResolversOverLocal'],
]);

const LocalOnlyResolvers: Set<ResolverNames> = new Set([
    // Persona Queries
    'contactPersona',
    'personas',
    'shadowPdvPersona',

    // Other resolvers
    'subscribeToConversationNotifications',
    'notificationSound',
    'notificationSoundOption',
    'notificationSoundFileName',
]);

const WorkerLocalResolvers: Set<ResolverNames> = new Set([
    'clearDirectoryHandleSetting',
    'directoryHandleSetting',
]);

const LocalOnlySharedResolvers: Set<ResolverNames> = new Set([
    // Persona Queries
    'peopleAppAccounts',
    'pgalSyncState',
    'peopleAppPersonaSearch',

    // Persona Mutations
    'bulkDeleteContacts',
    'updateColumnsPreferences',
    'createContact',
    'updateContact',
    'deleteContact',
    'addTagForPerson',
    'removeTagFromPerson',
    'deleteContactTag',
    'updateContactTag',
    'updateLivePersonaEditorUserPreferences',
    'updateSortAndFilterPreferences',

    // Persona Subscriptions
    'subscribeToPersonasChangeNotifications',
    'contactsMutationEvent',
    'pgalSyncStateEvent',
    'isFavoritesChangedEvent',
    'isDisplayNameOrderChangedEvent',
]);

export const CalendarQueryResolvers: Set<ResolverNames> = new Set([
    'calendarEvents',
    'calendarGroups',
    'collab',
    'collabV2',
    'fullBirthdayCalendarEvent',
    'fullCalendarEvent',
    'getSchedule',
    'resolveTeamsChannel',
    'sharedCalendarAdditionalInfo',
    'subscribeToCalendarEventNotifications',
]);

export const MailQueryResolvers: Set<ResolverNames> = new Set([
    'conversation',
    'conversationNode',
    'conversationNodes',
    'conversationRows',
    'item',
    'items',
    'itemRows',
    'subscribeToConversationNotifications',
    'subscribeToRowNotifications',
    'nonBootUserConfiguration',
]);

export const MailMutateResolvers: Set<ResolverNames> = new Set([
    'createDraft',
    'createSmartResponse',
    'saveDraft',
    'saveSmartResponse',
    'discardDraft',
    'sendItem',
    'undoSaveSend',
]);

export const FolderQueryResolvers: Set<ResolverNames> = new Set([
    'folderHierarchy',
    'folder',
    'folders',
    'startAccountData',
]);

const FolderMutateResolvers: Map<ResolverNames, GlobalFeatureName | undefined> = new Map([
    ['renameFolder', 'fwk-offline-mail'],
    ['createFolder', 'fwk-offline-mail'],
    ['moveFolder', 'fwk-offline-mail'],
    ['deleteFolder', 'fwk-offline-mail'],
]);

const FavoriteMutateResolvers: Set<ResolverNames> = new Set(['addFavorite', 'removeFavorite']);

// Calendar offline idb mutators will be enabled by flights. When fully graduated, then removing the entry should be enough
const CalendarMutateResolvers: Map<ResolverNames, GlobalFeatureName> = new Map([
    ['createCalendarEvent', 'cal-offline-event-create'],
    ['deleteCalendarEvent', 'cal-offline-event-cancel-delete'],
    ['cancelCalendarEvent', 'cal-offline-event-cancel-delete'],
    ['updateCalendarEvent', 'cal-offline-event-update'],
]);

const AttachmentIdbMutateResolvers: Map<ResolverNames, GlobalFeatureName | undefined> = new Map([
    ['addAttachmentToDraft', 'fwk-offline-insert-delete-attachments'],
    ['deleteAttachmentFromDraft', 'fwk-offline-insert-delete-attachments'],
    ['deleteLocalAttachment', 'fwk-offline-insert-delete-attachments'],
]);

export function enabledForWeb(
    op: 'Query' | 'Mutation' | 'Subscription',
    resolver: string,
    skipRemoteCallForcefully: boolean,
    resolverPolicy?: ResolverPolicy
): boolean {
    if (resolverPolicy === 'localOnly') {
        return false;
    }
    const enabled = getEnabled(op, resolver, skipRemoteCallForcefully);
    return (enabled & ResolverEnabledFor.Web) == ResolverEnabledFor.Web;
}

// The preferred pattern for offline capable mutations is:
// - submit a queuedaction and have the idbresolver persist the local lie associated with the mutation
// - have the action queue execute the webresolver when it can (e.g., when the user is back online)
//
// Executing an idb mutation without queuing the webresolver portion can lead to the local lie being
// persisted without the webresolver being executed. This can lead to inconsistencies between the local and remote state.
//
// But, there are some cases where the idb resolver executes both the online portion of the mutation and the local lie
// without attempting queuing.  Either both succeed or both fail.  For these cases, execute the idb resolver on an opt-in basis.
export function enabledForIdbNonQueuedMutation(
    scope: string, // parent of the resolver: 'Mutation', 'Persona', 'Conversation', etc
    resolver: string
): boolean {
    const allowedResolvers: ResolverNames[] = [
        'bulkDeleteContacts',
        'createContact',
        'updateContact',
        'deleteContact',
        'addTagForPerson',
        'addPersonToFavorites',
        'removePersonFromFavorites',
        'removeTagFromPerson',
        'deleteContactTag',
        'updateContactTag',
        'updateColumnsPreferences',
        'updateLivePersonaEditorUserPreferences',
        'updateSortAndFilterPreferences',

        // Person source Mutations
        'deletePersonSources',
        'unlinkPersonSources',
        'updatePersonSource',

        // Notification Sound Mutations
        'storeNotificationSound',
        'storeNotificationSoundOption',

        // Delay Send Mutation (IDB-only, no server equivalent)
        'cancelDelaySend',

        // Attachment cleanup mutation (IDB-only, no server equivalent)
        'deleteLocalAttachment',
    ];
    if (scope === 'Mutation' && allowedResolvers.includes(resolver as ResolverNames)) {
        return true;
    }

    return false;
}

export function enabledForIdb(
    op: 'Query' | 'Mutation' | 'Subscription',
    resolverName: string,
    skipRemoteCallForcefully: boolean,
    resolverPolicy?: ResolverPolicy
): boolean {
    const resolver = resolverName as ResolverNames;

    if (resolverPolicy === 'serverOnly') {
        return false;
    }

    if (CalendarQueryResolvers.has(resolver) && !isGlobalFeatureEnabled('fwk-offline-calendar')) {
        return false;
    }

    if (CalendarMutateResolvers.has(resolver)) {
        const flightName = CalendarMutateResolvers.get(resolver);
        if (
            !isGlobalFeatureEnabled('fwk-offline-calendar') ||
            !flightName ||
            !isGlobalFeatureEnabled(flightName)
        ) {
            return false;
        }
    }

    if (MailQueryResolvers.has(resolver) && !isGlobalFeatureEnabled('fwk-offline-mail')) {
        return false;
    }
    if (
        MailMutateResolvers.has(resolver) &&
        (!isGlobalFeatureEnabled('fwk-offline-mail') ||
            !isGlobalFeatureEnabled('cmp-offline-mail-newdraft'))
    ) {
        return false;
    }

    if (FolderQueryResolvers.has(resolver) && !isGlobalFeatureEnabled('fwk-offline-mail')) {
        return false;
    }

    if (FolderMutateResolvers.has(resolver)) {
        const flightName = FolderMutateResolvers.get(resolver);
        if (
            !isGlobalFeatureEnabled('fwk-offline-mail') ||
            !flightName ||
            !isGlobalFeatureEnabled(flightName)
        ) {
            return false;
        }
    }

    if (FavoriteMutateResolvers.has(resolver) && !isGlobalFeatureEnabled('fwk-offline-mail')) {
        return false;
    }

    if (AttachmentIdbMutateResolvers.has(resolver)) {
        const flightName = AttachmentIdbMutateResolvers.get(resolver);
        if (!flightName || !isGlobalFeatureEnabled(flightName)) {
            return false;
        }
    }
    const enabled = getEnabled(op, resolver, skipRemoteCallForcefully);
    return (enabled & ResolverEnabledFor.Idb) == ResolverEnabledFor.Idb;
}

export function enabledForRemote(
    op: 'Query' | 'Mutation' | 'Subscription',
    resolver: string,
    skipRemoteCallForcefully: boolean,
    resolverPolicy?: ResolverPolicy
): boolean {
    if (resolverPolicy === 'localOnly') {
        return false;
    }
    const enabled = getEnabled(op, resolver, skipRemoteCallForcefully);
    return (enabled & ResolverEnabledFor.Remote) == ResolverEnabledFor.Remote;
}

// The following function determines whether or not the resolver will go through the Outlook Gateway or not
// If you want the resolver to have a scorecard during the roll-out, please create a client-side flight, add the mapping to the IndividualResolverFeatureMappings list
// If you do this, it will ignore any setting related to this resolver name in the ECS setting.
// After the resolver API is graduated, you can move it to ECS to act a killswitch.
// If you do not want the resolver to have a scorecard during the rollout, you can add the configuration of the resolver to ECS directly.
function getEnabled(
    op: 'Query' | 'Mutation' | 'Subscription',
    resolverName: string,
    skipRemoteCallForcefully: boolean
) {
    const resolver = resolverName as ResolverNames;

    const computedLocalOnlyResolvers = getComputedLocalOnlyResolvers();
    let defaultEnabled = ResolverEnabledFor.All;
    if (computedLocalOnlyResolvers.has(resolver)) {
        defaultEnabled = ResolverEnabledFor.Idb;
    } else if (WorkerLocalResolvers.has(resolver)) {
        defaultEnabled = ResolverEnabledFor.Web;
    }

    if (IndividualResolverFeatureMappings.has(resolver)) {
        const featureName = IndividualResolverFeatureMappings.get(resolver);
        if (featureName) {
            return isGlobalFeatureEnabled(featureName as GlobalFeatureName) &&
                !skipRemoteCallForcefully
                ? ResolverEnabledFor.Remote
                : defaultEnabled;
        }
    }

    const resolverLocations = getApplicationSettings('EnabledResolvers');
    const configuredEnabled = resolverLocations?.[op]?.[resolver];
    return configuredEnabled == undefined ? defaultEnabled : configuredEnabled;
}

// Ensure the required LocalOnlySharedResolvers are enabled in online mode by excluding them from the returned set
function getComputedLocalOnlyResolvers(): Set<ResolverNames> {
    if (isGlobalFeatureEnabled('peo-online-via-pdv')) {
        return LocalOnlyResolvers;
    }

    if (isGlobalFeatureEnabled('fwk-exp-scorecard-test')) {
        // Scorecard pipeline test flight — no product behavior
    }

    return new Set([...LocalOnlyResolvers, ...LocalOnlySharedResolvers]);
}
